import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { usePlan, usePlanModules } from '../../../hooks/subscriptions';
import { formatPrice, formatBillingInterval, formatResourceLimit, getCategoryLabel } from '../../../utils/subscriptions';

export const PlanShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: plan, isLoading, error } = usePlan(id!);
  const { data: planModules, isLoading: modulesLoading } = usePlanModules(id!);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load plan'}
        </Alert>
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box>
        <Alert severity="info">Plan not found</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subscriptions/plans')}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">{plan.name}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {plan.slug}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/subscriptions/plans')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/subscriptions/plans/edit/${id}`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Details Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Details
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">{plan.description}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pricing
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {formatPrice(plan.basePrice, plan.currency)}
                    <Typography component="span" variant="body1" color="text.secondary" ml={1}>
                      {formatBillingInterval(plan.billingInterval)}
                    </Typography>
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom mb={2}>
                    Resource Limits
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max Users
                      </Typography>
                      <Typography variant="h6">
                        {formatResourceLimit(plan.maxUsers)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max Stores
                      </Typography>
                      <Typography variant="h6">
                        {formatResourceLimit(plan.maxStores)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max Warehouses
                      </Typography>
                      <Typography variant="h6">
                        {formatResourceLimit(plan.maxWarehouses)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Display Order
                  </Typography>
                  <Typography variant="body1">{plan.displayOrder}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={plan.isActive ? 'Active' : 'Inactive'}
                    color={plan.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Modules Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Modules
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {modulesLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : planModules && planModules.length > 0 ? (
                <Stack spacing={1.5}>
                  {planModules.map((pm) => (
                    <Box
                      key={pm.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {pm.module?.name || 'Unknown Module'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pm.module?.key}
                          </Typography>
                        </Box>
                        {pm.isIncluded ? (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="Included"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
                            label="Add-on"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {pm.module?.category && (
                        <Chip
                          label={getCategoryLabel(pm.module.category)}
                          size="small"
                          sx={{ mt: 1, fontSize: '0.7rem' }}
                        />
                      )}
                      {pm.addOnPrice != null && pm.addOnPrice > 0 && (
                        <Typography variant="caption" color="primary.main" display="block" mt={1}>
                          Add-on: {formatPrice(pm.addOnPrice, plan.currency)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No modules assigned to this plan yet.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Metadata Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timestamps
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(plan.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Updated At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(plan.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
